import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Waves, Film, Flame, Wifi, ChefHat, Dumbbell, Tv, Wind, 
  Bath, Sailboat, Anchor, MapPin, Home, Thermometer, 
  Sparkles, Bed, CircleDot, Fish, Trees, Clock, Ban,
  PawPrint, Cigarette, Calendar, Phone, Car, Zap, Shirt,
  UtensilsCrossed, Gamepad2, Music, Sun, Mountain, Check
} from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const iconMap = {
  waves: Waves,
  film: Film,
  flame: Flame,
  wifi: Wifi,
  'chef-hat': ChefHat,
  dumbbell: Dumbbell,
  tv: Tv,
  wind: Wind,
  bath: Bath,
  sailboat: Sailboat,
  anchor: Anchor,
  'map-pin': MapPin,
  home: Home,
  thermometer: Thermometer,
  sparkles: Sparkles,
  bed: Bed,
  circle: CircleDot,
};

// Comprehensive features from the old website
const allFeatures = [
  { name: "In-ground Heated Swimming Pool", icon: Waves },
  { name: "HD Projection Home Theatre", icon: Film },
  { name: "5 Person Hot Tub", icon: Bath },
  { name: "3 Fireplaces", icon: Flame },
  { name: "Fitness Gym/Spa", icon: Dumbbell },
  { name: "Billiards Table", icon: Gamepad2 },
  { name: "High Speed Internet & WiFi", icon: Wifi },
  { name: "Air Conditioning", icon: Wind },
  { name: "Gourmet Kitchen with Viking Gas Stove", icon: ChefHat },
  { name: "8 Flat Screen Plasma/LCD/LED TVs", icon: Tv },
  { name: "Canoe, Kayak & Paddle Boat", icon: Sailboat },
  { name: "Large Dock with Water Trampoline", icon: Anchor },
  { name: "Poker & Black Jack Tables", icon: Gamepad2 },
  { name: "Satellite Dishes", icon: Tv },
  { name: "Netflix Included", icon: Film },
  { name: "Blue Ray/DVD/CD Player & Stereo", icon: Music },
  { name: "Washer & Dryer", icon: Shirt },
  { name: "Dishwasher & Microwave", icon: UtensilsCrossed },
  { name: "Large Gas BBQ", icon: Flame },
  { name: "Emergency Power Generator", icon: Zap },
  { name: "Jacuzzi Tubs", icon: Bath },
  { name: "Patio Furniture", icon: Sun },
  { name: "Landscape Lighting", icon: Sparkles },
];

// Special features from the old website
const specialFeatures = [
  "162 feet of shoreline",
  "1.42 acre lot",
  "Cathedral wood ceiling & floor to ceiling windows in great room",
  "Sky lights throughout",
  "Heated floors in master bath",
  "Numerous sliding door walkouts",
  "Automatic lights outside",
  "Large decks and Patio",
  "Fitness Gym/Spa overlooking the lake",
  "Beautifully furnished & decorated",
  "Linens provided (towels & bedding)",
  "Weekly cleaning service included",
];

// Area information
const areaInfo = [
  { icon: Trees, text: "Private & Secluded - next to Peterborough Wildlife Preserve" },
  { icon: Mountain, text: "Spectacular views of Jack Lake" },
  { icon: Fish, text: "Excellent fishing - muskie, bass, walleye" },
  { icon: Car, text: "10 minutes to the Town of Apsley" },
  { icon: MapPin, text: "Just over 2 hours from downtown Toronto" },
];

export default function AmenitiesPage() {
  const [property, setProperty] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [bedrooms, setBedrooms] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propRes, amenRes, bedroomRes, galleryRes] = await Promise.all([
          axios.get(`${API}/property`),
          axios.get(`${API}/amenities`),
          axios.get(`${API}/bedrooms`),
          axios.get(`${API}/gallery?category=floor_plans`),
        ]);
        setProperty(propRes.data);
        setAmenities(amenRes.data);
        setBedrooms(bedroomRes.data);
        setGallery(galleryRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 container-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl"
        >
          <p className="text-caption text-[#D4AF37] mb-4">Amenities</p>
          <h1 className="heading-display text-[#1C1C1C] mb-6">
            Every Comfort Awaits
          </h1>
          <p className="text-body-lg text-[#66605B]">
            This luxurious 5,500 sq. ft. residence is set on 3 acres next to Peterborough Wildlife Preserve, 
            just over 2 hours from downtown Toronto. The comfortable interior is bright and open with numerous 
            skylights and walkouts. Big Bear Estate is designed for entertaining, with rooms on three levels 
            including a large cathedral wood ceiling & stone fireplace in the great room.
          </p>
        </motion.div>
      </section>

      {/* Property Overview Stats */}
      <section className="container-padding pb-16">
        <div className="bg-[#1B4D3E] text-white p-8 md:p-12 rounded-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl md:text-5xl font-serif text-[#D4AF37]">
                {property?.bedrooms || 7}
              </p>
              <p className="text-white/70 mt-2">Bedrooms</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-serif text-[#D4AF37]">
                {property?.bathrooms || 5}
              </p>
              <p className="text-white/70 mt-2">Bathrooms</p>
              <p className="text-white/50 text-xs mt-1">(3 en-suites)</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-serif text-[#D4AF37]">
                {property?.sleeps || 15}
              </p>
              <p className="text-white/70 mt-2">Guests</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-serif text-[#D4AF37]">
                5,500+
              </p>
              <p className="text-white/70 mt-2">Sq. Feet</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bedrooms Section - Detailed */}
      <section className="container-padding pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <p className="text-caption text-[#D4AF37] mb-4">Accommodations</p>
          <h2 className="heading-section text-[#1C1C1C] mb-4">
            Sleeping Arrangements
          </h2>
          <p className="text-[#66605B] max-w-2xl">
            7 beautifully appointed bedrooms across three levels, each with its own TV 
            and thoughtful amenities for your comfort.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {bedrooms.map((bedroom, idx) => (
            <motion.div
              key={bedroom.id || idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="bg-white p-6 rounded-sm shadow-sm border-l-4 border-[#1B4D3E]"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1B4D3E] text-white rounded-full flex items-center justify-center font-serif text-xl flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-serif text-xl text-[#1C1C1C]">{bedroom.name}</h3>
                    <span className="text-xs bg-[#F0EBE5] text-[#1B4D3E] px-2 py-1 rounded">
                      {bedroom.level}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-[#1C1C1C]">
                      <Bed className="w-4 h-4 text-[#D4AF37]" />
                      <span className="font-medium">{bedroom.bed_type}</span>
                    </div>
                    <p className="text-[#66605B] pl-6">{bedroom.amenities}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-[#F0EBE5] section-padding">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-caption text-[#D4AF37] mb-4">Features</p>
            <h2 className="heading-section text-[#1C1C1C]">
              Premium Amenities
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {allFeatures.map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.02 }}
                  className="flex items-center gap-3 p-4 bg-white rounded-sm shadow-sm"
                >
                  <div className="w-10 h-10 bg-[#1B4D3E]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-[#1B4D3E]" />
                  </div>
                  <span className="text-sm text-[#1C1C1C]">{feature.name}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Special Features */}
      <section className="section-padding">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mb-8"
              >
                <p className="text-caption text-[#D4AF37] mb-4">Special</p>
                <h2 className="heading-section text-[#1C1C1C]">
                  Unique Property Features
                </h2>
              </motion.div>

              <div className="space-y-3">
                {specialFeatures.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.03 }}
                    className="flex items-start gap-3"
                  >
                    <Check className="w-5 h-5 text-[#1B4D3E] mt-0.5 flex-shrink-0" />
                    <span className="text-[#66605B]">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Area Information */}
            <div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mb-8"
              >
                <p className="text-caption text-[#D4AF37] mb-4">Location</p>
                <h2 className="heading-section text-[#1C1C1C]">
                  Area Information
                </h2>
              </motion.div>

              <div className="bg-[#1B4D3E] p-6 rounded-sm text-white">
                <div className="space-y-4">
                  {areaInfo.map((info, idx) => {
                    const IconComponent = info.icon;
                    return (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex items-start gap-3"
                      >
                        <IconComponent className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <span className="text-white/90">{info.text}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floor Plans */}
      {gallery.length > 0 && (
        <section className="bg-[#F0EBE5] section-padding">
          <div className="container-padding">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mb-12 text-center"
            >
              <p className="text-caption text-[#D4AF37] mb-4">Layout</p>
              <h2 className="heading-section text-[#1C1C1C]">
                Floor Plans
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {gallery.map((img) => (
                <motion.div 
                  key={img.id} 
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-white p-4 rounded-sm shadow-sm"
                >
                  <img 
                    src={getImageUrl(img.url)} 
                    alt={img.alt || "Floor plan"}
                    className="w-full h-auto rounded"
                  />
                  {img.alt && (
                    <p className="text-center text-sm text-[#66605B] mt-3">{img.alt}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Conditions & Policies */}
      <section className="bg-[#1B4D3E] text-white section-padding">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-serif text-3xl md:text-4xl mb-4">
                House Policies & Conditions
              </h2>
              <p className="text-white/70">
                Please review our rental conditions before booking
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <Clock className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                <p className="text-[#D4AF37] font-semibold mb-2">Check-in</p>
                <p className="text-white/80">{property?.check_in || 'Friday 4:30 PM'}</p>
              </div>
              <div className="text-center">
                <Clock className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                <p className="text-[#D4AF37] font-semibold mb-2">Check-out</p>
                <p className="text-white/80">{property?.check_out || 'Friday 11:00 AM'}</p>
              </div>
              <div className="text-center">
                <Calendar className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                <p className="text-[#D4AF37] font-semibold mb-2">Rental Period</p>
                <p className="text-white/80">Weekly Only (Fri to Fri)</p>
              </div>
            </div>

            <div className="border-t border-white/20 pt-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex items-center justify-center gap-3 bg-white/10 p-4 rounded-sm">
                  <Ban className="w-6 h-6 text-red-400" />
                  <div>
                    <p className="font-semibold">No Smoking</p>
                    <p className="text-xs text-white/60">Indoor & outdoor areas</p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 bg-white/10 p-4 rounded-sm">
                  <PawPrint className="w-6 h-6 text-red-400" />
                  <div>
                    <p className="font-semibold">No Pets</p>
                    <p className="text-xs text-white/60">Service animals excepted</p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 bg-white/10 p-4 rounded-sm">
                  <Calendar className="w-6 h-6 text-red-400" />
                  <div>
                    <p className="font-semibold">No Weekend Only</p>
                    <p className="text-xs text-white/60">Full week minimum</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="mt-12 text-center">
              <p className="text-white/70 mb-4">Questions about our amenities or policies?</p>
              <a 
                href="tel:416-543-4749" 
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#1B4D3E] px-6 py-3 rounded-sm font-semibold hover:bg-[#c9a432] transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call 416.543.4749
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
